import { useQuery } from '@apollo/client'
import { useState, useRef, useEffect, useCallback } from 'react'
import { Outlet, useLocation, useParams } from 'react-router-dom'

import { useNavigationGuard, useAuth } from '../../hooks'
import { detectActiveContext } from '../../components/context-switcher/compute-contexts'
import { Container, Loading, NavTabs, Notice, Row, Stack, Text } from '../../components'
import { queries } from '../../graphql'

export function Profile() {
  const profileFormRef = useRef(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [submissionStatus, setSubmissionStatus] = useState('none')
  const navigationGuard = useNavigationGuard()
  const { pathname } = useLocation()
  const { fundId: fundIdParam } = useParams()
  const [{ impersonationType }] = useAuth()
  const { data: { me } = {}, loading } = useQuery(queries.user.me)
  const isCharityUser = (me?.charityUsers || []).length > 0
  const hasRealDonorFunds = (me?.funds || []).some((f) => !f.name?.startsWith('charity-'))
  const isPureCharityUser = isCharityUser && !hasRealDonorFunds
  const donorFundId = fundIdParam
    ? Number(fundIdParam)
    : me?.funds?.find((f) => !f.name?.startsWith('charity-') && !f.closed)?.id
  const fundProfile = me?.funds?.find((f) => f.id === donorFundId)?.fundProfile
  const address = fundProfile?.address
  // Determine active portal kind from the current URL
  const activePortal = detectActiveContext(pathname)
  const portalKind = activePortal?.kind ?? (isPureCharityUser ? 'charity' : 'fund')

  const firstLogin = !address && !isPureCharityUser && portalKind !== 'advisor' && portalKind !== 'charity'

  // Fetch funds to conditionally show the Advisors tab.
  const { data: fundsData } = useQuery(queries.funds.myFunds, { fetchPolicy: 'cache-first' })
  const hasFunds = (fundsData?.me?.funds?.length ?? 0) > 0
  const showAdvisorManagement = hasFunds && impersonationType !== 'ADVISOR'

  // Advisor portal tabs — universal User Details + Delete User bookend the advisor-specific tabs
  const advisorTabs = [
    { label: 'User Details', to: 'user-details' },
    { label: 'Personal Details', to: 'personal-details' },
    { label: 'Security', to: 'security' },
    { label: 'Custodian Settings', to: 'custodian-settings' },
    { label: 'Delete', to: 'delete' },
  ]

  const donorPortalTabGroups = [
    {
      title: 'User',
      tabs: [
        { label: 'User Details', to: 'user-details' },
        { label: 'Security', to: 'security' },
      ],
    },
    {
      title: 'Current Giving Fund',
      tabs: [
        { label: 'Giving Fund', to: 'donor-details' },
        { label: 'Payment Methods', to: 'payment-methods' },
        ...(showAdvisorManagement ? [{ label: 'Advisors', to: 'advisor-management' }] : []),
        { label: 'Succession', to: 'successor-requests' },
      ],
    },
    {
      title: null,
      tabs: [{ label: 'Delete', to: 'delete' }],
    },
  ]

  const charityTabs = [
    { label: 'User Details', to: 'user-details' },
    { label: 'Representative Details', to: 'representative-details' },
    { label: 'Security', to: 'security' },
    { label: 'Delete', to: 'delete' },
  ]

  const getFlatTabs = () => {
    if (portalKind === 'advisor') return advisorTabs
    if (portalKind === 'charity') return charityTabs
    return null
  }
  const tabs = getFlatTabs()
  const groups = tabs ? null : donorPortalTabGroups

  const submitUnsavedChanges = useCallback(() => {
    profileFormRef.current?.requestSubmit()
  }, [])

  const onSubmitSuccess = useCallback(() => {
    setHasUnsavedChanges(false)
    setSubmissionStatus('success')
    navigationGuard.onSubmitSuccess()
  }, [navigationGuard])

  const onSubmitFail = useCallback(() => {
    navigationGuard.onSubmitFail()
  }, [navigationGuard])

  const discardUnsavedChanges = useCallback(() => {
    setHasUnsavedChanges(false)
  }, [])

  // Register/unregister unsaved changes with navigation guard
  useEffect(() => {
    if (hasUnsavedChanges) {
      navigationGuard.registerUnsavedChanges(submitUnsavedChanges, discardUnsavedChanges)
    } else {
      navigationGuard.unregisterUnsavedChanges()
    }
  }, [hasUnsavedChanges, navigationGuard, submitUnsavedChanges, discardUnsavedChanges])

  if (loading) return <Loading />
  return (
    <Container maxWidth="lg">
      <Stack spacing={4} sx={{ mt: '2rem' }}>
        <Row sx={{ width: '100%' }} justifyContent="space-between" alignItems="center">
          <Stack sx={{ width: '200%', maxWidth: '100vw' }}>
            <Text.H4>My Profile</Text.H4>
          </Stack>
        </Row>
        {firstLogin && (
          <Notice>
            <Text.H4 data-testid="welcomeTitle">Welcome to GiveWise!</Text.H4>
            <Text.Body data-testid="welcomeContent">
              You must update your profile with your address and phone number in order to complete your registration
            </Text.Body>
          </Notice>
        )}
        <Stack spacing={0}>
          <NavTabs
            hasUnsavedChanges={hasUnsavedChanges}
            discardUnsavedChanges={discardUnsavedChanges}
            submitUnsavedChanges={submitUnsavedChanges}
            submissionStatus={submissionStatus}
            setSubmissionStatus={setSubmissionStatus}
            {...(groups ? { groups } : { tabs })}
          />
          <Outlet context={{ setHasUnsavedChanges, profileFormRef, onSubmitSuccess, onSubmitFail, donorFundId }} />
        </Stack>
      </Stack>
    </Container>
  )
}
